import type { BasicProductAttributes, CustomClaims, RMUser } from "../webapp/src/types";

function validMail(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const mail = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail) ? mail : undefined;
}

/** Only capture a mailbox from the Microsoft profile matching the signed-in identity. */
export function getMicrosoftRMMail(
    loginEmail: string,
    profile: { mail?: string | null; userPrincipalName?: string | null } | undefined,
): string | undefined {
    const mail = validMail(profile?.mail);
    const login = loginEmail.trim().toLowerCase();
    if (!mail || !login) return undefined;
    return profile?.userPrincipalName?.trim().toLowerCase() === login || mail.toLowerCase() === login
        ? mail : undefined;
}

export function withRMMail(rm: RMUser, claims: Pick<CustomClaims, "rm_mail"> | undefined): RMUser {
    const mail = validMail(claims?.rm_mail);
    return mail ? { ...rm, mail } : rm;
}

const sameEmail = (email: string | undefined, canonical: string): boolean => email?.trim().toLowerCase() === canonical;

/**
 * Record the requester's identity and mailbox without reassigning credit: rm/referredBy stay as
 * submitted, entries naming the requester gain the mailbox, and a requester who is not the rm is
 * recorded as submittedBy.
 */
export function trackRequesterRM<T extends BasicProductAttributes>(product: T, requester: RMUser | undefined): T {
    const mail = requester?.mail;
    if (!requester || !mail) return product;
    const requesterEmail = requester.email.trim().toLowerCase();
    const tracked = { ...product };
    for (const key of ["rm", "referredBy", "submittedBy"] as const) {
        const rm = tracked[key];
        if (rm && sameEmail(rm.email, requesterEmail)) tracked[key] = { ...rm, mail };
    }
    if (!sameEmail(tracked.rm?.email, requesterEmail) && !tracked.submittedBy) tracked.submittedBy = requester;
    return tracked;
}

/** A migrated certified staff member can act as an RM without a legacy profile document. */
export function createClaimRM(
    identity: { email?: string; name?: string },
    claims: CustomClaims | undefined,
): RMUser | undefined {
    const email = identity.email?.trim().toLowerCase();
    if (!email || claims?.staff !== true || claims.kfh_rm_certified !== true) return undefined;
    const rmId = /^([0-9]+)@kfh\.com$/.exec(email)?.[1];
    return withRMMail({
        id: email,
        email,
        ...(rmId ? { rm_id: rmId } : {}),
        name_en: identity.name?.trim() || email,
        name_ar: "",
        active: true,
        certified: true,
        role: "rm",
        ...(claims.segment ? { segment: claims.segment } : {}),
        ...(claims.availableSegments ? { availableSegments: claims.availableSegments } : {}),
    }, claims);
}

